/**
 * 视频激活二次确认弹窗（参考电商复刻视觉语言）
 *
 * 显示首帧缩略图、模型 / 时长 / 预计积分 / 余额。
 */

import { Loader2, Wand2, X, Coins, Sparkles } from 'lucide-react';
import { resolveBrowserMediaUrl } from '../../../../utils/storageUrl';

interface Props {
  open: boolean;
  previewUrl: string | null;
  sourceLabel: string;
  modelName: string;
  duration: number;
  estimatedCredits: number;
  currentCredits: number;
  isReactivate?: boolean;
  submitting?: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}

export function ActivateConfirmDialog(props: Props) {
  const {
    open,
    previewUrl,
    sourceLabel,
    modelName,
    duration,
    estimatedCredits,
    currentCredits,
    isReactivate,
    submitting,
    onCancel,
    onConfirm,
  } = props;

  if (!open) return null;

  const insufficient = currentCredits < estimatedCredits;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="flex w-full max-w-md flex-col overflow-hidden rounded-[28px] bg-white shadow-2xl shadow-slate-950/30">
        {/* 顶部 hero */}
        <div className="relative overflow-hidden bg-gradient-to-br from-slate-950 via-slate-900 to-slate-800 p-5 text-white">
          <div className="absolute right-[-40px] top-[-40px] h-32 w-32 rounded-full bg-sky-400/20 blur-2xl" />
          <div className="relative flex items-start justify-between gap-3">
            <div>
              <div className="mb-1.5 inline-flex items-center gap-1.5 rounded-full bg-white/10 px-2.5 py-0.5 text-[11px] font-medium text-slate-200">
                <Sparkles className="h-3 w-3 text-sky-200" />
                {isReactivate ? '重新激活' : '激活视频'}
              </div>
              <h2 className="text-lg font-semibold leading-tight">
                {isReactivate ? '重新生成本镜视频' : `使用${sourceLabel}生成视频`}
              </h2>
            </div>
            <button
              type="button"
              onClick={onCancel}
              className="rounded-full bg-white/10 p-1.5 text-white transition hover:bg-white/20"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
        </div>

        <div className="space-y-4 bg-slate-50 p-5">
          {/* 参考图预览 */}
          <div className="aspect-video overflow-hidden rounded-2xl bg-slate-100 ring-1 ring-slate-200">
            {previewUrl ? (
              <img src={resolveBrowserMediaUrl(previewUrl)} alt={sourceLabel} className="h-full w-full object-cover" />
            ) : (
              <div className="flex h-full w-full items-center justify-center text-xs text-slate-400">
                {sourceLabel}加载中
              </div>
            )}
          </div>

          {/* 信息卡片 */}
          <div className="grid grid-cols-2 gap-2">
            <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-100">
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">模型</div>
              <div className="mt-1 truncate text-sm font-semibold text-slate-900">{modelName}</div>
            </div>
            <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-100">
              <div className="text-[11px] uppercase tracking-[0.18em] text-slate-400">时长</div>
              <div className="mt-1 text-sm font-semibold text-slate-900">{duration} 秒</div>
            </div>
          </div>

          {/* 积分 */}
          <div className="rounded-2xl bg-white p-3 ring-1 ring-slate-100">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-1.5 text-[11px] uppercase tracking-[0.18em] text-slate-400">
                <Coins className="h-3 w-3" /> 预计消耗
              </div>
              <div className="text-base font-semibold text-sky-700">{estimatedCredits} 积分</div>
            </div>
            <div className="mt-2 flex items-center justify-between text-[11px]">
              <span className="text-slate-400">当前余额</span>
              <span className={insufficient ? 'text-rose-500' : 'text-slate-600'}>
                {currentCredits} 积分
              </span>
            </div>
          </div>

          {isReactivate && (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 px-3 py-2.5 text-xs text-amber-800">
              重新激活会归档旧视频任务，扣新积分。旧视频不退款。
            </div>
          )}

          {insufficient && (
            <div className="rounded-2xl border border-rose-200 bg-rose-50 px-3 py-2.5 text-xs text-rose-700">
              积分不足，请先充值或兑换
            </div>
          )}
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 bg-white px-5 py-3">
          <button
            type="button"
            onClick={onCancel}
            className="rounded-2xl border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700 transition hover:bg-slate-50"
          >
            取消
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={submitting || insufficient}
            className="inline-flex items-center gap-2 rounded-2xl bg-slate-950 px-4 py-2 text-sm font-semibold text-white shadow-md transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Wand2 className="h-4 w-4" />}
            确认激活
          </button>
        </div>
      </div>
    </div>
  );
}
